<template>
  <base-file-drop :disabled="disabled" @add:files="onFileDrop">
    <template #default="{ dragOver }">
      <component
        :is="hasFiles || box ? 'div' : 'label'"
        :tabindex="box && !disabled ? '0' : null"
        :for="hasFiles ? null : inputId"
        class="tw-flex tw-flex-col tw-w-full game-tile-radius tw-overflow-hidden tw-group"
        :class="[
          {
            'mod-input--focus-within':
              !hasFiles && !hasErrors && !disabled && !box && !dragOver,
            'tw-cursor-pointer': !hasFiles && !disabled,
            'tw-border-danger tw-border-2': hasErrors && !dragOver,
            'game-primary-button-border-color tw-border-2': dragOver,
          },
          customClass,
          boxStyle(dragOver),
        ]"
        v-bind="$attrs"
        @click="() => (box ? fileInput.click() : null)"
        @keydown.enter="() => (box ? fileInput.click() : null)"
      >
        <slot />

        <base-button
          v-if="button"
          :id="inputId"
          full-width
          :danger="hasErrors"
          :primary="!secondary"
          :secondary="secondary"
          icon="upload"
          icon-size="lg"
          :disabled="disabled"
          :radius="radius"
          :status="status"
          :progress="progress"
          @click="() => fileInput.click()"
        >
          <span>{{ label }}</span>
        </base-button>

        <input
          :id="inputId"
          ref="fileInput"
          :disabled="disabled || readonly"
          type="file"
          class="tw-hidden"
          :multiple="multiple"
          :accept="accept"
          @change="onFileChange"
        />
      </component>
    </template>
  </base-file-drop>

  <div
    v-if="hasErrors || hint"
    class="tw-flex tw-flex-col mod-hint-gap tw-space-y-1"
  >
    <base-input-errors :errors="errors" align="left" />

    <base-input-hint v-if="hint" :id="`${inputId}Help`" align="center">
      {{ hint }}
    </base-input-hint>
  </div>
</template>
<script>
import BaseFileDrop from '@shared/components/Misc/BaseFileDrop.vue'
import { computed, toRefs, inject, ref } from 'vue'
import { genHtmlId } from '@helpers/utils.js'
import { isFrontend } from '@config'
export default {
  components: {
    BaseFileDrop,
  },
  props: {
    errors: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Upload',
    },
    hint: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: String,
      default: '',
    },
    accept: {
      type: String,
      default: '',
    },
    hasFiles: {
      type: Boolean,
      default: false,
    },
    radius: {
      type: String,
      default: 'bottom',
    },
    status: {
      type: String,
      default: null,
    },
    progress: {
      type: Number,
      default: 0,
    },
    button: {
      type: Boolean,
      default: true,
    },
    box: {
      type: Boolean,
      default: false,
    },
    testId: {
      type: String,
      default: '',
    },
  },

  emits: ['file:upload'],
  setup(props, { emit }) {
    const { multiple, errors, box } = toRefs(props)

    const inputId = genHtmlId()
    const fileInput = ref(null)
    const readonly = inject('saving', false)

    const hasErrors = computed(() => !!errors.value?.length)
    const boxStyle = computed(
      () =>
        (dragOver = false) =>
          box.value
            ? [
                'tw-text-center tw-cursor-pointer tw-border-2 tw-border-dashed tw-p-6 md:tw-min-h-60 hover:tw-brightness-110 focus:tw-brightness-110 tw-transition-all tw-flex tw-flex-col tw-items-center tw-justify-center',
                { 'tw-brightness-110 tw-border-primary': dragOver },
                isFrontend
                  ? 'tw-border-dark-1 tw-bg-dark mod-global--border-radius focus:tw-border-primary tw-outline-none'
                  : 'game-primary-button-border-color game-input-background-color game-tile-radius',
              ]
            : ''
    )

    function onFileChange(e) {
      if (!e.target.files[0]) return

      const payload = multiple.value ? e.target.files : e.target.files[0]
      emit('file:upload', payload)
      e.target.value = ''
    }

    function onFileDrop(files) {
      const payload = multiple.value ? files : files[0]
      emit('file:upload', payload)
    }

    return {
      onFileChange,
      onFileDrop,
      isFrontend,
      hasErrors,
      fileInput,
      readonly,
      boxStyle,
      inputId,
    }
  },
}
</script>
